/**
 * WHICH DOCUMENTS THIS BOOKING IS ALREADY TOO LATE TO SEND — `tasks/practice-documents.md` §23.1.1.
 *
 * A document linked "2 weeks before" against an appointment booked in 2 days names a day that has
 * already gone. `runSendDueDocumentsJob` fires on the day the rule NAMES, so that ask would never
 * send — and before this prompt existed the only trace was a `skipped` banner discovered later, on
 * a patient file nobody was looking at.
 *
 * ── THIS DOES NOT DECIDE ANYTHING. THE SERVER DOES ───────────────────────────────────────────
 *
 * `patientAppointmentsController` says so in its own header: *"THE SERVER DECIDES WHAT IS LATE.
 * NOT THE CLIENT."* Lateness is measured there, on every booking, so that one made through a path
 * that never prompts — an older client, an import, an integration — still produces the same
 * visible `skipped` row. That silence is what §23.1 exists to prevent, and tying the outcome to a
 * prompt's answer would reopen it.
 *
 * So everything here answers ONE question and only that one: **is there anything worth asking the
 * user about before this booking is saved?** A wrong answer costs a prompt that should not have
 * appeared, or a prompt that should have and did not — never a wrong row. That is why this is
 * allowed to be an approximation of the server's calendar arithmetic, and why a failed trigger
 * read simply asks nothing.
 *
 * ── A PURE MODULE, WITH THE CLOCK PASSED IN ──────────────────────────────────────────────────
 *
 * No Angular, no `moment`, no `new Date()` reached for internally. The whole of this feature's
 * awkwardness is calendar-day arithmetic across a timezone, and a rule that reads the clock itself
 * can only be tested by moving the clock.
 */

/** One row of the appointment type's links, as the booking screen needs it. */
export interface LateDocumentLink {
  /** The LINK id — what `documentDecisions` names back to the server. Not the document id. */
  id: string;
  practiceDocumentId: string;
  name?: string | null;
  sendOn: string;
  offsetDays: number | null;
}

/** One link the practice is about to be asked about. */
export interface LateDocumentDecision {
  linkId: string;
  practiceDocumentId: string;
  /** For the prompt's own sentence. Never an id. */
  name: string;
  /** The day the rule named, `YYYY-MM-DD` — already in the past. */
  dueDay: string;
  /** How the rule reads, e.g. "14 days before". */
  rule: string;
}

/** `YYYY-MM-DD` for a local date, without dragging `moment` into a pure module. */
export function localDay(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * The day a link names, or null when it names none.
 *
 * Mirrors `utils/patientDocumentRequestUtils.computeDocumentDueAt`, including the two rules that
 * are easy to lose:
 *
 *   · `confirmation` is sent NOW and is therefore NEVER late — the server returns today for it;
 *   · the SIGN LIVES IN `sendOn`, so `offsetDays` is taken as a MAGNITUDE. A negative offset on a
 *     `before` link must not quietly become an `after`.
 *
 * Day arithmetic is done at UTC noon, as the server does, so a ± few hours of timezone offset can
 * never roll the answer onto the wrong calendar day.
 */
export function ruleDueDay(
  sendOn: string,
  offsetDays: number | null | undefined,
  appointmentDay: string,
): string | null {
  if (sendOn === 'confirmation') return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(appointmentDay || '').trim())) return null;

  const offset = Number.isFinite(Number(offsetDays)) ? Math.abs(Number(offsetDays)) : 0;
  const base = new Date(`${appointmentDay.trim()}T12:00:00.000Z`);
  if (Number.isNaN(base.getTime())) return null;

  base.setUTCDate(base.getUTCDate() + (sendOn === 'after' ? offset : -offset));
  return base.toISOString().slice(0, 10);
}

/** How the rule reads, for the prompt's sentence. */
export function rulePhrase(sendOn: string, offsetDays: number | null | undefined): string {
  const offset = Number.isFinite(Number(offsetDays)) ? Math.abs(Number(offsetDays)) : 0;
  if (sendOn === 'confirmation') return 'when the appointment is confirmed';
  const days = offset === 1 ? '1 day' : `${offset} days`;
  return sendOn === 'after' ? `${days} after the appointment` : `${days} before the appointment`;
}

/**
 * The links this booking is already too late to send.
 *
 * `selectedDocumentIds` is the booking picker's selection and is honoured exactly as the server
 * honours `practiceDocumentIds`: **null means "no picker ran, the links decide"**, which is not
 * the same as an empty array, which means the user unticked everything. Prompting about a document
 * somebody has just unticked would be asking them to reverse a decision they made ten seconds ago.
 *
 * Deduped by LINK, not by document: one appointment type legitimately carries several links of the
 * same document — at confirmation and two weeks before — and only the late one is asked about.
 */
export function lateDocumentDecisions(
  links: LateDocumentLink[] | null | undefined,
  appointmentDay: string,
  now: Date,
  selectedDocumentIds: string[] | null,
): LateDocumentDecision[] {
  if (!Array.isArray(links) || !links.length) return [];

  const today = localDay(now);
  const chosen = Array.isArray(selectedDocumentIds)
    ? new Set(selectedDocumentIds.map((id) => String(id || '').trim().toLowerCase()))
    : null;

  const decisions: LateDocumentDecision[] = [];
  const seen = new Set<string>();

  for (const link of links) {
    if (!link || !link.id || !link.practiceDocumentId) continue;
    if (chosen && !chosen.has(String(link.practiceDocumentId).trim().toLowerCase())) continue;

    const dueDay = ruleDueDay(link.sendOn, link.offsetDays, appointmentDay);
    if (!dueDay || dueDay >= today) continue;

    const key = String(link.id).trim().toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);

    decisions.push({
      linkId: link.id,
      practiceDocumentId: link.practiceDocumentId,
      name: (link.name || '').trim() || 'A practice document',
      dueDay,
      rule: rulePhrase(link.sendOn, link.offsetDays),
    });
  }

  return decisions;
}

/**
 * The lines the confirmation dialog lists, one per document.
 *
 * `FeedbackService.showConfirmation` takes a `data` array and renders a row per entry, which the
 * slot-conflict confirm beside this already uses.
 */
export function lateDocumentLines(decisions: LateDocumentDecision[]): string[] {
  return decisions.map((d) => `${d.name} — normally sent ${d.rule}, which was ${d.dueDay}.`);
}

/** What the server is told: the links the practice was asked about AND accepted. */
export function sendNowDecisions(decisions: LateDocumentDecision[])
  : { linkId: string; action: 'send_now' }[] {
  return decisions.map((d) => ({ linkId: d.linkId, action: 'send_now' }));
}
